// Combat state shared between Phaser scenes (driver) and Preact overlay (HUD).
//
// The Phaser CombatScene is the source of truth: it owns the SRS draws,
// the failure scaffold state machine, the speech recognizer, and the
// monster health bookkeeping. The HUD is a thin presentation layer.

import type { Card } from '../content/cardSchema';
import type { Outcome } from '../speech/scoring';
import type { MicState } from '../speech/mic';

export type Phase =
  | 'idle'                         // no encounter
  | 'show-card'                    // card visible, mic idle
  | 'listening'                    // hold-to-speak active
  | 'scoring'                      // ASR pending
  | 'feedback'                     // showing score result
  | 'scaffold-audio'               // step-1 retry: replay native audio
  | 'scaffold-pinyin'              // step-2 retry: pinyin + stroke order
  | 'scaffold-choral'              // step-3: say-with-recording
  | 'resolved';                    // encounter done, fading out

export interface CombatState {
  phase: Phase;
  card: Card | null;
  retryStep: 0 | 1 | 2 | 3;
  showPinyin: boolean;
  showEnglish: boolean;             // shown on step >= 2
  lastScore: number | null;
  lastOutcome: Outcome | null;
  lastHeard: string;
  heroHp: number;
  heroMaxHp: number;
  monsterHp: number;
  monsterMaxHp: number;
  monsterName: string;
  monsterEmoji: string;
  micState: MicState;
  micLastError?: string;
}

type Listener = (s: CombatState) => void;
type Action =
  | { kind: 'pressMic' }
  | { kind: 'releaseMic' }
  | { kind: 'replayAudio' }
  | { kind: 'sageWhisper' }
  | { kind: 'grantMic' }
  | { kind: 'exit' };
type ActionListener = (a: Action) => void;

class CombatBus {
  private state: CombatState = initialState();
  private listeners = new Set<Listener>();
  private actionListeners = new Set<ActionListener>();

  get(): CombatState { return this.state; }

  on(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => { this.listeners.delete(fn); };
  }

  set(patch: Partial<CombatState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn(this.state);
  }

  reset(): void {
    this.state = initialState();
    for (const fn of this.listeners) fn(this.state);
  }

  // UI → game actions
  onAction(fn: ActionListener): () => void {
    this.actionListeners.add(fn);
    return () => { this.actionListeners.delete(fn); };
  }
  dispatch(a: Action): void {
    for (const fn of this.actionListeners) fn(a);
  }
}

function initialState(): CombatState {
  return {
    phase: 'idle',
    card: null,
    retryStep: 0,
    showPinyin: false,
    showEnglish: false,
    lastScore: null,
    lastOutcome: null,
    lastHeard: '',
    heroHp: 5,
    heroMaxHp: 5,
    monsterHp: 1,
    monsterMaxHp: 1,
    monsterName: '',
    monsterEmoji: '👾',
    micState: 'unknown',
  };
}

export const combatBus = new CombatBus();
